import React, { useContext, useState } from 'react';
import { AgFinancingCreditAnalysisStatus, AgFinancingInput, Phase2, AgFinancingPhase2Status } from '@indigo-ag/schema';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { SingleRow } from '../../shared/SingleRow';
import { PhaseSection, CreditUtil, WithConditionalTooltip, tooltipHelperForDisabledElements } from 'shared';
import { CreditAnalysisState, ToggleOnHoldCreditAnalysis } from '../../shared/credit-analysis';
import { StartAnalysisModal } from '../../phases/phase2/StartAnalysisModal/StartAnalysisModal';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { StartAnalysisPhase2Disclaimer } from '../../shared/StartAnalysisPhase2Disclaimer/StartAnalysisPhase2Disclaimer';
import { UserContext } from 'core/components/GateWay/GateWay';
import { financingAgFinancing, upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import { getCreditApprovalData } from 'features/brazil-grower/queries/creditApproval.queries';

interface CreditSectionProps {
  creditInfo: Phase2['credit'];
  title: string;
}

export const CreditSection: React.FunctionComponent<CreditSectionProps> = ({ creditInfo, title }) => {
  /**
   * State
   */
  const [openStartAnalysis, setOpenStartAnalysis] = useState(false);
  const [isRestartingAnalysis, setIsRestartingAnalysis] = useState(false);

  /**
   * Finance Process Context
   */
  const { agFinance } = useContext(FinanceProcessContext);

  /**
   * User Context
   */
  const { isSuper } = useContext(UserContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  const handleCloseStartAnalysis = () => {
    setOpenStartAnalysis(false);
  };

  const { addFlashMessage } = useFlashMessage();

  const restartCreditAnalysis = () => {
    setIsRestartingAnalysis(true);

    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinance?.id,
      phase2: {
        status:
          agFinance?.phase2?.status === AgFinancingPhase2Status.DENIED ||
          agFinance?.phase2?.status === AgFinancingPhase2Status.PENDING_RESTART
            ? AgFinancingPhase2Status.IN_PROGRESS
            : undefined,
        credit: {
          status: AgFinancingCreditAnalysisStatus.STARTED
        }
      }
    };

    upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      },
      refetchQueries: [
        {
          // * Refetch credit approval data so the user gets the live data on the decision sheet after reopen credit analysis
          query: getCreditApprovalData,
          variables: { agFinancingId: agFinance?.id }
        },
        {
          // * Refetch negotiation details to show the correct view in the credit approval section
          query: financingAgFinancing,
          variables: { agFinancingId: agFinance?.id }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        addFlashMessage({
          content: `${i18next.t('creditAnalysis')} ${i18next.t('restarted')}`,
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsRestartingAnalysis(false);
      });
  };

  const isOnHold = agFinance?.phase2?.status === AgFinancingPhase2Status.ON_HOLD;
  const canStartCredit = CreditUtil.canStart(agFinance?.canStartPhase2, agFinance?.phase2);
  const canViewStartButton = agFinance?.phase2?.status !== AgFinancingPhase2Status.AWAITING_COMMERCIAL_DEAL;

  const renderStartButton = () => {
    return !CreditUtil.hasStarted(creditInfo) && canViewStartButton ? (
      <>
        <WithConditionalTooltip visible={!canStartCredit} title={<StartAnalysisPhase2Disclaimer />} placement="top">
          <Button
            variant="primary"
            size="small"
            onClick={() => setOpenStartAnalysis(true)}
            disabled={(!isSuper && !canStartCredit) || isOnHold}
            style={tooltipHelperForDisabledElements(!isSuper && !canStartCredit)}
          >
            {i18next.t('start')}
          </Button>
        </WithConditionalTooltip>
        <StartAnalysisModal
          open={openStartAnalysis}
          startExternalProcess={{ flc: true, luchesi: false, terramagna: false }}
          closeModal={handleCloseStartAnalysis}
        />
      </>
    ) : (
      undefined
    );
  };

  const renderReopenButton = () => {
    return CreditUtil.canReopen(agFinance?.phase2) ? (
      <Button
        variant="primary"
        size="small"
        className="text-transform-capitalize"
        onClick={() => restartCreditAnalysis()}
        busy={isRestartingAnalysis}
      >
        {i18next.t('reopen')}
      </Button>
    ) : null;
  };

  return (
    <PhaseSection
      title={title}
      topRightContent={
        <>
          {renderStartButton()}
          {renderReopenButton()}
          <ToggleOnHoldCreditAnalysis />
        </>
      }
    >
      {creditInfo?.startedAt ? (
        <CreditAnalysisState creditInfo={creditInfo} />
      ) : (
        <SingleRow title={i18next.t('status')} content={i18next.t('notStarted')} />
      )}
    </PhaseSection>
  );
};
